import * as i0 from '@angular/core';
import { SimpleChange, Component, ViewEncapsulation, Optional, Self, Input, HostBinding, HostListener, NgModule } from '@angular/core';
import * as i1 from '@angular/forms';
import { SelectControlValueAccessor, SelectMultipleControlValueAccessor } from '@angular/forms';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { takeUntil, filter, startWith, tap } from 'rxjs/operators';
import { Subject, Observable } from 'rxjs';
import { EuiInputTextComponent } from '@eui/components/eui-input-text';
import { CommonModule } from '@angular/common';

class EuiSelectComponent {
    constructor(_renderer, _elementRef, control, viewContainerRef) {
        this._renderer = _renderer;
        this._elementRef = _elementRef;
        this.control = control;
        this.viewContainerRef = viewContainerRef;
        this.destroy$ = new Subject();
        this.element = _elementRef.nativeElement;
        this.renderer = _renderer;
        // set random id
        this.setIdAttribute();
    }
    get isInvalid() {
        return this._isInvalid || null;
    }
    set isInvalid(state) {
        this.setInvalid(state);
    }
    get multiple() {
        return this._multiple;
    }
    set multiple(value) {
        this._multiple = coerceBooleanProperty(value) ? true : undefined;
    }
    get id() {
        return this._id;
    }
    set id(value) {
        this.setIdAttribute(value);
    }
    get cssClasses() {
        return this.getCssClasses();
    }
    get readonly() {
        return this._readonly;
    }
    set readonly(value) {
        this._readonly = coerceBooleanProperty(value);
    }
    ngAfterContentInit() {
        const select = this._elementRef.nativeElement;
        new Observable(observer => {
            const callback = (e) => observer.next(e);
            select.addEventListener('DOMNodeInserted', callback);
            return () => select.removeEventListener('DOMNodeInserted', callback);
        }).pipe(takeUntil(this.destroy$), filter(e => this._placeholder && e.target !== this._placeholder)).subscribe(() => {
            this.renderer.removeChild(select, this._placeholder);
            this._placeholder = null;
            this.ngOnChanges({ placeholder: new SimpleChange(null, this.placeholder, true) });
        });
        if (this.control) {
            // listen to value changes for undefined and set it to placeholder if that exists.
            this.control.valueChanges.pipe(takeUntil(this.destroy$), startWith(this.control.value), tap(() => {
                // in case of readonly create the inputElement while NG content
                // has been initialized and selectedOptions are available.
                if (this.readonly) {
                    this.setReadonlyValue(this.readonlyInput);
                }
            }), filter(() => this._placeholder !== undefined)).subscribe(value => {
                this.renderer.removeClass(this._elementRef.nativeElement, 'eui-select__placeholder');
                if (value === undefined) {
                    select.options[0].selected = true;
                }
                // if (value !== undefined) {
                //     select.options[0].innerText = '';
                // }
                if (value === this._placeholder.value) {
                    this.control.control.setValue(undefined);
                    select.options[0].innerText = this.placeholder;
                    this.renderer.addClass(this._elementRef.nativeElement, 'eui-select__placeholder');
                }
                // in case value is null and there's a placeholder select the placeholder
                if (value === null && this.placeholder) {
                    select.options[0].selected = true;
                    select.options[0].innerText = this.placeholder;
                    this.renderer.addClass(this._elementRef.nativeElement, 'eui-select__placeholder');
                }
                /**
                 * In case placeholder's innerText is the same as the displayed value of the element then
                 * add class to grey the colour.
                 */
                if (this._elementRef.nativeElement.value === this._placeholder.innerText) {
                    this.renderer.addClass(this._elementRef.nativeElement, 'eui-select__placeholder');
                }
                else {
                    this.renderer.removeClass(this._elementRef.nativeElement, 'eui-select__placeholder');
                }
            });
        }
        else {
            // in case of readonly create the inputElement while NG content
            // has been initialized and selectedOptions are available.
            if (this.readonly) {
                this.setReadonlyValue(this.readonlyInput);
            }
        }
    }
    ngOnChanges(changes) {
        if (changes['placeholder']) {
            // in case option Element already created update the value
            if (this._placeholder) {
                this.renderer.setProperty(this._placeholder, 'innerText', changes['placeholder'].currentValue);
            }
            else {
                // create option Element
                this._placeholder = this.renderer.createElement('option');
                this.renderer.addClass(this._placeholder, 'eui-select__placeholder');
                this.renderer.setProperty(this._placeholder, 'innerText', changes['placeholder'].currentValue);
                // set attributes to act as a placeholder
                // this.renderer.setAttribute(this._placeholder, 'disabled', '');
                if (this.control && !this.control?.value) {
                    this.renderer.setAttribute(this._placeholder, 'selected', '');
                }
                else if (!this.control) {
                    // if no control exists then check if any other option is already
                    // selected otherwise set placeholder as selected.
                    const select = this._elementRef.nativeElement;
                    // check against element attribute (and not JS Object attribute)
                    const selected = Array.from(select.options).find(i => i.hasAttribute('selected') === true);
                    // if none is selected then placeholder must be selected
                    if (!selected) {
                        this.renderer.setAttribute(this._placeholder, 'selected', '');
                    }
                }
                // append option element as the first of the select children
                const parent = this.element;
                parent.insertBefore(this._placeholder, parent.firstChild);
                if (this._elementRef.nativeElement.value === changes['placeholder'].currentValue) {
                    this.renderer.addClass(this._elementRef.nativeElement, 'eui-select__placeholder');
                }
                if (this.control && !this.control?.value) {
                    this.control.valueAccessor.writeValue(undefined);
                    this.control.control.setValue(undefined);
                }
            }
        }
        if (changes['multiple'] && this.control) {
            if (!this._multiple) {
                this.control.valueAccessor = new SelectControlValueAccessor(this._renderer, this._elementRef);
            }
            else {
                let value = this.control.valueAccessor.value, _optionMap = this.control.valueAccessor['_optionMap'], _idCounter = this.control.valueAccessor['_idCounter'] || 0;
                // TODO: find a better way to instantiate the valueAccessor (scenario switching  from
                //  SelectControlValueAccessor to Multiple also to be tested).
                this.control.valueAccessor = new EuiSelectMultipleControlValueAccessor(this._renderer, this._elementRef);
                this.control.valueAccessor.value = value;
                this.control.valueAccessor['_optionMap'] = _optionMap;
                this.control.valueAccessor['_idCounter'] = _idCounter;
            }
        }
        if (changes['readonly']) {
            if (this.readonly) {
                // hide select element
                this.renderer.setAttribute(this.element, 'readonly', 'true');
                this.renderer.setAttribute(this.element, 'hidden', '');
                // create an euiInputComponent to hold the value (for accessibility reasons)
                this.readonlyInput = this.createReadonlyElement();
                this.renderer.insertBefore(this._elementRef.nativeElement.parentElement, this._elementRef.nativeElement, this.readonlyInput.location.nativeElement);
                this.setReadonlyValue(this.readonlyInput);
            }
            else {
                this.readonlyInput?.destroy();
                this.renderer.removeAttribute(this._elementRef.nativeElement, 'readonly');
                this.renderer.removeAttribute(this.element, 'hidden');
            }
        }
    }
    ngDoCheck() {
        if (this.control) {
            this._isInvalid = this.control.invalid && this.control.touched;
        }
    }
    ngOnDestroy() {
        this.destroy$.complete();
        this.destroy$.unsubscribe();
    }
    /**
     * Listener that triggers when selected value changes
     * @param selected the value which is selected
     */
    valueChanges(selected) {
        // if selected value matches placeholder then set colour to grey
        if (selected === this.placeholder) {
            this.renderer.addClass(this._elementRef.nativeElement, 'eui-select__placeholder');
        }
        else {
            this.renderer.removeClass(this._elementRef.nativeElement, 'eui-select__placeholder');
        }
        if (this.readonly) {
            this.setReadonlyValue(this.readonlyInput);
        }
    }
    /**
     * calculates and set's the invalid state based on the provided one. If NG_VALUE_ACCESSOR is provided
     * then control invalid attribute has priority.
     * @param state
     * @protected
     */
    setInvalid(state) {
        // in case it's controlled by NgControl override
        this._isInvalid = this.control ? this.control.invalid && this.control.touched : coerceBooleanProperty(state);
    }
    /**
     * returns string of classes
     * @protected
     */
    getCssClasses() {
        return [
            'eui-select',
            this._isInvalid ? `eui-select--invalid` : '',
        ].join(' ').trim();
    }
    /**
     * set the id attribute of the element
     * @param value the ID attribute (https://www.w3schools.com/html/html_id.asp)
     * @protected
     */
    setIdAttribute(value = null) {
        this._id = value ? value : `select_${Math.floor(Math.random() * 1000000)}`;
    }
    /**
     * set readonly value from selected options presented as comma separated string
     * @param readonlyRef the input element to set the value
     * @private
     */
    setReadonlyValue(readonlyRef) {
        if (readonlyRef) {
            const selectedOptions = Array.apply(null, this._elementRef.nativeElement.selectedOptions)
                .map(i => i.text)
                .filter(i => i !== this.placeholder)
                .join(', ');
            this.renderer.setProperty(readonlyRef.location.nativeElement, 'value', selectedOptions);
        }
    }
    /**
     * create readonly element
     * @private
     */
    createReadonlyElement() {
        const componentRef = this.viewContainerRef.createComponent(EuiInputTextComponent);
        componentRef.instance.readonly = true;
        return componentRef;
    }
}
/** @nocollapse */ EuiSelectComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiSelectComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i1.NgControl, optional: true, self: true }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiSelectComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiSelectComponent, selector: "select[euiSelect]", inputs: { isInvalid: "isInvalid", multiple: "multiple", id: "id", readonly: "readonly", placeholder: "placeholder" }, host: { listeners: { "change": "valueChanges($event.target.value)" }, properties: { "attr.multiple": "this.multiple", "attr.id": "this.id", "class": "this.cssClasses" } }, usesOnChanges: true, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [".eui-select{font: 400 1rem/1.25rem arial,sans-serif;-webkit-appearance:none;appearance:none;background-color:var(--eui-base-color-white);border:1px solid var(--eui-base-color-grey-50);border-radius:var(--eui-base-border-radius);color:var(--eui-base-color-grey-100);display:block;padding:calc(var(--eui-base-spacing-s) - 1px);width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--eui-base-color)}.eui-select:active:not([readonly]),.eui-select:focus:not([readonly]){z-index:auto;border-color:var(--eui-base-color-accessible-focus)!important;box-shadow:inset 0 0 0 1px var(--eui-base-color-accessible-focus),inset 0 0 0 2px var(--eui-base-color-grey-5);transition:border-color .15s ease-in-out,box-shadow .15s ease-in-out}.eui-select[readonly]{background-color:transparent;border-color:transparent!important;box-shadow:none;height:inherit;padding:var(--eui-base-spacing-2xs) 0;pointer-events:none}.eui-select[readonly]+.eui-feedback-message,.eui-select[readonly]+.eui-input-maxlength{-webkit-appearance:none;appearance:none;display:none}.eui-select[disabled],.eui-select--disabled{background-color:var(--eui-base-color-grey-5);border:1px solid var(--eui-base-color-grey-25);color:var(--eui-base-color-grey-75);pointer-events:none}.eui-select--invalid,.eui-select--danger{border:1px solid var(--eui-base-color-danger-100)}.eui-select--clearable{padding-right:var(--eui-base-spacing-2xl)}.eui-select--clearable-icon{visibility:visible;position:absolute;right:var(--eui-base-spacing-s);cursor:pointer;-webkit-user-select:none;user-select:none}.eui-select--clearable-icon svg.eui-icon-svg{fill:var(--eui-base-color-grey-35)}.eui-select--clearable-icon svg.eui-icon-svg:hover{fill:var(--eui-base-color-grey-100)}.eui-select--loading{padding-right:var(--eui-base-spacing-2xl)}.eui-select--loading-icon{align-items:center;display:flex}.eui-select--loading-icon:after,.eui-select--loading-icon:before{content:\"\";height:var(--eui-base-spacing-l);position:absolute;right:var(--eui-base-spacing-s);width:var(--eui-base-spacing-l)}.eui-select--loading-icon:before{border:var(--eui-base-spacing-2xs) solid rgba(0,0,0,.35);border-radius:100%}.eui-select--loading-icon:after{animation:eui-loading .6s linear;animation-iteration-count:infinite;border-color:var(--eui-base-color-white) transparent transparent;border-radius:100%;border-style:solid;border-width:var(--eui-base-spacing-2xs);box-shadow:0 0 0 1px transparent}.eui-select--loading-icon--sm:after,.eui-select--loading-icon--sm:before{height:var(--eui-base-spacing-m);width:var(--eui-base-spacing-m)}@keyframes eui-loading{to{transform:rotate(360deg)}}.eui-select::-webkit-scrollbar{display:inherit;height:8px;width:8px;background-color:var(--eui-base-color-grey-10)}.eui-select::-webkit-scrollbar-thumb{background-color:var(--eui-base-color-grey-20);border-radius:5rem}.eui-select::-webkit-scrollbar-thumb:hover{background-color:var(--eui-base-color-grey-25)}.eui-select::-webkit-scrollbar-track{background-color:var(--eui-base-color-grey-10);border-radius:0}.eui-select[readonly][multiple=true] option:checked{background:none}.eui-select[readonly][multiple=true] :not(option:checked){display:none}.eui-select[multiple=true],.eui-select[size]:not([size=\"1\"]):not([size=\"0\"]){background-image:none;height:auto;overflow:auto;padding:var(--eui-base-spacing-2xs) 0}.eui-select__readonly:active,.eui-select__readonly:focus{border-color:var(--eui-base-color-accessible-focus)!important;box-shadow:inset 0 0 0 1px var(--eui-base-color-accessible-focus),inset 0 0 0 2px var(--eui-base-color-grey-5);transition:border-color .15s ease-in-out,box-shadow .15s ease-in-out;border:2px solid var(--eui-base-color-accessible-focus);outline:none;transition:border .15s ease-in-out}.eui-select__placeholder{color:var(--eui-base-color-grey-50)!important}.eui-select__item{color:var(--eui-base-color-grey-100)}.eui-select optgroup{font: 400 1rem/1.25rem arial,sans-serif;font-style:normal;font-weight:700}.eui-select optgroup,.eui-select option{-webkit-appearance:none;appearance:none;color:var(--eui-base-color);display:block;padding:var(--eui-base-spacing-2xs);white-space:pre}.eui-select optgroup:disabled,.eui-select option:disabled{color:var(--eui-base-color-grey-25)}.eui-select:not([readonly]):not([multiple]),.eui-select[size=\"1\"],.eui-select[size=\"0\"]{-webkit-appearance:none;appearance:none;background-color:var(--eui-base-color-white);background-image:url('data:image/svg+xml;utf8, <svg focusable=\"false\" viewBox=\"0 0 16 16\" xmlns=\"http://www.w3.org/2000/svg\" name=\"chevron-down\" aria-label=\"Close menu\" role=\"img\"><path d=\"M8 11L3 6 3.7 5.3 8 9.6 12.3 5.3 13 6z\"/></svg>');background-position:calc(100% - var(--eui-base-spacing-s)) center;background-repeat:no-repeat;background-size:var(--eui-base-spacing-m);padding-right:var(--eui-base-spacing-xl)}\n"], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'select[euiSelect]', template: '<ng-content></ng-content>', encapsulation: ViewEncapsulation.None, styles: [".eui-select{font: 400 1rem/1.25rem arial,sans-serif;-webkit-appearance:none;appearance:none;background-color:var(--eui-base-color-white);border:1px solid var(--eui-base-color-grey-50);border-radius:var(--eui-base-border-radius);color:var(--eui-base-color-grey-100);display:block;padding:calc(var(--eui-base-spacing-s) - 1px);width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--eui-base-color)}.eui-select:active:not([readonly]),.eui-select:focus:not([readonly]){z-index:auto;border-color:var(--eui-base-color-accessible-focus)!important;box-shadow:inset 0 0 0 1px var(--eui-base-color-accessible-focus),inset 0 0 0 2px var(--eui-base-color-grey-5);transition:border-color .15s ease-in-out,box-shadow .15s ease-in-out}.eui-select[readonly]{background-color:transparent;border-color:transparent!important;box-shadow:none;height:inherit;padding:var(--eui-base-spacing-2xs) 0;pointer-events:none}.eui-select[readonly]+.eui-feedback-message,.eui-select[readonly]+.eui-input-maxlength{-webkit-appearance:none;appearance:none;display:none}.eui-select[disabled],.eui-select--disabled{background-color:var(--eui-base-color-grey-5);border:1px solid var(--eui-base-color-grey-25);color:var(--eui-base-color-grey-75);pointer-events:none}.eui-select--invalid,.eui-select--danger{border:1px solid var(--eui-base-color-danger-100)}.eui-select--clearable{padding-right:var(--eui-base-spacing-2xl)}.eui-select--clearable-icon{visibility:visible;position:absolute;right:var(--eui-base-spacing-s);cursor:pointer;-webkit-user-select:none;user-select:none}.eui-select--clearable-icon svg.eui-icon-svg{fill:var(--eui-base-color-grey-35)}.eui-select--clearable-icon svg.eui-icon-svg:hover{fill:var(--eui-base-color-grey-100)}.eui-select--loading{padding-right:var(--eui-base-spacing-2xl)}.eui-select--loading-icon{align-items:center;display:flex}.eui-select--loading-icon:after,.eui-select--loading-icon:before{content:\"\";height:var(--eui-base-spacing-l);position:absolute;right:var(--eui-base-spacing-s);width:var(--eui-base-spacing-l)}.eui-select--loading-icon:before{border:var(--eui-base-spacing-2xs) solid rgba(0,0,0,.35);border-radius:100%}.eui-select--loading-icon:after{animation:eui-loading .6s linear;animation-iteration-count:infinite;border-color:var(--eui-base-color-white) transparent transparent;border-radius:100%;border-style:solid;border-width:var(--eui-base-spacing-2xs);box-shadow:0 0 0 1px transparent}.eui-select--loading-icon--sm:after,.eui-select--loading-icon--sm:before{height:var(--eui-base-spacing-m);width:var(--eui-base-spacing-m)}@keyframes eui-loading{to{transform:rotate(360deg)}}.eui-select::-webkit-scrollbar{display:inherit;height:8px;width:8px;background-color:var(--eui-base-color-grey-10)}.eui-select::-webkit-scrollbar-thumb{background-color:var(--eui-base-color-grey-20);border-radius:5rem}.eui-select::-webkit-scrollbar-thumb:hover{background-color:var(--eui-base-color-grey-25)}.eui-select::-webkit-scrollbar-track{background-color:var(--eui-base-color-grey-10);border-radius:0}.eui-select[readonly][multiple=true] option:checked{background:none}.eui-select[readonly][multiple=true] :not(option:checked){display:none}.eui-select[multiple=true],.eui-select[size]:not([size=\"1\"]):not([size=\"0\"]){background-image:none;height:auto;overflow:auto;padding:var(--eui-base-spacing-2xs) 0}.eui-select__readonly:active,.eui-select__readonly:focus{border-color:var(--eui-base-color-accessible-focus)!important;box-shadow:inset 0 0 0 1px var(--eui-base-color-accessible-focus),inset 0 0 0 2px var(--eui-base-color-grey-5);transition:border-color .15s ease-in-out,box-shadow .15s ease-in-out;border:2px solid var(--eui-base-color-accessible-focus);outline:none;transition:border .15s ease-in-out}.eui-select__placeholder{color:var(--eui-base-color-grey-50)!important}.eui-select__item{color:var(--eui-base-color-grey-100)}.eui-select optgroup{font: 400 1rem/1.25rem arial,sans-serif;font-style:normal;font-weight:700}.eui-select optgroup,.eui-select option{-webkit-appearance:none;appearance:none;color:var(--eui-base-color);display:block;padding:var(--eui-base-spacing-2xs);white-space:pre}.eui-select optgroup:disabled,.eui-select option:disabled{color:var(--eui-base-color-grey-25)}.eui-select:not([readonly]):not([multiple]),.eui-select[size=\"1\"],.eui-select[size=\"0\"]{-webkit-appearance:none;appearance:none;background-color:var(--eui-base-color-white);background-image:url('data:image/svg+xml;utf8, <svg focusable=\"false\" viewBox=\"0 0 16 16\" xmlns=\"http://www.w3.org/2000/svg\" name=\"chevron-down\" aria-label=\"Close menu\" role=\"img\"><path d=\"M8 11L3 6 3.7 5.3 8 9.6 12.3 5.3 13 6z\"/></svg>');background-position:calc(100% - var(--eui-base-spacing-s)) center;background-repeat:no-repeat;background-size:var(--eui-base-spacing-m);padding-right:var(--eui-base-spacing-xl)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i1.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }, { type: i0.ViewContainerRef }]; }, propDecorators: { isInvalid: [{
                type: Input
            }], multiple: [{
                type: HostBinding,
                args: ['attr.multiple']
            }, {
                type: Input
            }], id: [{
                type: HostBinding,
                args: ['attr.id']
            }, {
                type: Input
            }], cssClasses: [{
                type: HostBinding,
                args: ['class']
            }], readonly: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], valueChanges: [{
                type: HostListener,
                args: ['change', ['$event.target.value']]
            }] } });
/**
 * Extend the SelectMultipleControlValueAccessor to meet eUI requirements
 */
class EuiSelectMultipleControlValueAccessor extends SelectMultipleControlValueAccessor {
    constructor(_renderer, _elementRef) {
        super(_renderer, _elementRef);
        this.elementRef = _elementRef;
    }
    /**
     * Override writeValue and check for null value when form resets
     * @param value The selected value or null in case of form reset
     */
    writeValue(value) {
        super.writeValue(value);
        // in case value is null set it null too
        if (!value) {
            this.elementRef.nativeElement.value = value;
        }
    }
}

class EuiSelectModule {
}
/** @nocollapse */ EuiSelectModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiSelectModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiSelectModule, declarations: [EuiSelectComponent], imports: [CommonModule], exports: [EuiSelectComponent] });
/** @nocollapse */ EuiSelectModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiSelectModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        EuiSelectComponent,
                    ],
                    exports: [
                        EuiSelectComponent,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { EuiSelectComponent, EuiSelectModule, EuiSelectMultipleControlValueAccessor };
